import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

// ============================================================================
// SIDEBAR COMPONENT TYPES
// ============================================================================

interface SidebarProps extends React.ComponentProps<"div"> {
  variant?: "default" | "system-settings";
  children: React.ReactNode;
}

interface SidebarHeaderProps extends React.ComponentProps<"div"> {
  children: React.ReactNode;
}

interface SidebarBodyProps extends React.ComponentProps<"div"> {
  children: React.ReactNode;
}

interface SidebarFooterProps extends React.ComponentProps<"div"> {
  children: React.ReactNode;
}

interface SidebarItemProps extends React.ComponentProps<"li"> {
  children: React.ReactNode;
  isActive?: boolean;
}

interface SidebarItemTitleProps extends React.ComponentProps<"div"> {
  children: React.ReactNode;
}

interface SidebarItemActionProps extends React.ComponentProps<"ul"> {
  children: React.ReactNode;
}

interface SidebarAccordionProps extends React.ComponentProps<"div"> {
  id: string;
  children: React.ReactNode;
}

interface SidebarAccordionItemProps extends React.ComponentProps<"div"> {
  id: string;
  children: React.ReactNode;
}

interface SidebarAccordionToggleProps extends React.ComponentProps<"div"> {
  "data-wl-toggle": string;
  "data-wl-target": string;
  "aria-expanded": boolean;
  children: React.ReactNode;
}

interface SidebarAccordionItemListProps extends React.ComponentProps<"div"> {
  id: string;
  "data-wl-parent-id": string;
  children: React.ReactNode;
}

// ============================================================================
// SIDEBAR COMPONENT
// ============================================================================

const sidebarVariants = cva("wl-sidebar", {
  variants: {
    variant: {
      default: "",
      "system-settings": "wl-system-settings",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

const Sidebar = React.forwardRef<
  HTMLDivElement,
  SidebarProps & VariantProps<typeof sidebarVariants>
>(({ variant = "default", className, children, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(sidebarVariants({ variant }), className)}
      {...props}
    >
      {children}
    </div>
  );
});
Sidebar.displayName = "Sidebar";

// ============================================================================
// SIDEBAR HEADER
// ============================================================================

const SidebarHeader = React.forwardRef<HTMLDivElement, SidebarHeaderProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div ref={ref} className={cn("wl-header", className)} {...props}>
        {children}
      </div>
    );
  }
);
SidebarHeader.displayName = "SidebarHeader";

// ============================================================================
// SIDEBAR BODY
// ============================================================================

const SidebarBody = React.forwardRef<HTMLDivElement, SidebarBodyProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div ref={ref} className={cn("wl-body", className)} {...props}>
        {children}
      </div>
    );
  }
);
SidebarBody.displayName = "SidebarBody";

// ============================================================================
// SIDEBAR FOOTER
// ============================================================================

const SidebarFooter = React.forwardRef<HTMLDivElement, SidebarFooterProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div ref={ref} className={cn("wl-footer", className)} {...props}>
        {children}
      </div>
    );
  }
);
SidebarFooter.displayName = "SidebarFooter";

// ============================================================================
// SIDEBAR ITEM COMPONENTS
// ============================================================================

const SidebarItem = React.forwardRef<HTMLLIElement, SidebarItemProps>(
  ({ className, children, isActive, ...props }, ref) => {
    return (
      <li
        ref={ref}
        className={cn(isActive && "wl-active", className)}
        {...props}
      >
        {children}
      </li>
    );
  }
);
SidebarItem.displayName = "SidebarItem";

const SidebarItemTitle = React.forwardRef<
  HTMLDivElement,
  SidebarItemTitleProps
>(({ className, children, ...props }, ref) => {
  return (
    <div ref={ref} className={cn("wl-title", className)} {...props}>
      {children}
    </div>
  );
});
SidebarItemTitle.displayName = "SidebarItemTitle";

const SidebarItemAction = React.forwardRef<
  HTMLUListElement,
  SidebarItemActionProps
>(({ className, children, ...props }, ref) => {
  return (
    <ul ref={ref} className={cn("wl-item-action", className)} {...props}>
      {children}
    </ul>
  );
});
SidebarItemAction.displayName = "SidebarItemAction";

// ============================================================================
// SIDEBAR ACCORDION COMPONENTS
// ============================================================================

const SidebarAccordion = React.forwardRef<
  HTMLDivElement,
  SidebarAccordionProps
>(({ className, children, ...props }, ref) => {
  return (
    <div ref={ref} className={cn("wl-accordion", className)} {...props}>
      {children}
    </div>
  );
});
SidebarAccordion.displayName = "SidebarAccordion";

const SidebarAccordionItem = React.forwardRef<
  HTMLDivElement,
  SidebarAccordionItemProps
>(({ className, children, ...props }, ref) => {
  return (
    <div ref={ref} className={cn("wl-accordion-item", className)} {...props}>
      {children}
    </div>
  );
});
SidebarAccordionItem.displayName = "SidebarAccordionItem";

const SidebarAccordionToggle = React.forwardRef<
  HTMLDivElement,
  SidebarAccordionToggleProps
>(({ className, children, ...props }, ref) => {
  return (
    <div ref={ref} className={cn("wl-accordion-toggle", className)} {...props}>
      {children}
    </div>
  );
});
SidebarAccordionToggle.displayName = "SidebarAccordionToggle";

const SidebarAccordionItemList = React.forwardRef<
  HTMLDivElement,
  SidebarAccordionItemListProps
>(({ className, children, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn("wl-accordion-item-list", className)}
      {...props}
    >
      {children}
    </div>
  );
});
SidebarAccordionItemList.displayName = "SidebarAccordionItemList";

// ============================================================================
// SIDEBAR UTILITY COMPONENTS
// ============================================================================

const SidebarItemTitleList = React.forwardRef<
  HTMLUListElement,
  React.ComponentProps<"ul">
>(({ className, children, ...props }, ref) => {
  return (
    <ul ref={ref} className={cn("wl-item-title-list", className)} {...props}>
      {children}
    </ul>
  );
});
SidebarItemTitleList.displayName = "SidebarItemTitleList";

const SidebarHeadTop = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, children, ...props }, ref) => {
  return (
    <div ref={ref} className={cn("wl-head-top", className)} {...props}>
      {children}
    </div>
  );
});
SidebarHeadTop.displayName = "SidebarHeadTop";

const SidebarWrapper = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, children, ...props }, ref) => {
  return (
    <div ref={ref} className={cn("wl-wrapper", className)} {...props}>
      {children}
    </div>
  );
});
SidebarWrapper.displayName = "SidebarWrapper";

const SidebarIconBlock = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, ...props }, ref) => {
  return (
    <div ref={ref} className={cn("wl-icon-block", className)} {...props} />
  );
});
SidebarIconBlock.displayName = "SidebarIconBlock";

const SidebarLogo = React.forwardRef<
  HTMLElement,
  React.ComponentProps<"figure">
>(({ className, children, ...props }, ref) => {
  return (
    <figure ref={ref} className={cn("wl-logo", className)} {...props}>
      {children}
    </figure>
  );
});
SidebarLogo.displayName = "SidebarLogo";

const SidebarAvatar = React.forwardRef<
  HTMLElement,
  React.ComponentProps<"figure">
>(({ className, children, ...props }, ref) => {
  return (
    <figure
      ref={ref}
      className={cn("wl-avatar wl-avatar-lg", className)}
      {...props}
    >
      {children}
    </figure>
  );
});
SidebarAvatar.displayName = "SidebarAvatar";

const SidebarInfoWrapper = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, children, ...props }, ref) => {
  return (
    <div ref={ref} className={cn("wl-info-wrapper", className)} {...props}>
      {children}
    </div>
  );
});
SidebarInfoWrapper.displayName = "SidebarInfoWrapper";

const SidebarSubTitle = React.forwardRef<
  HTMLSpanElement,
  React.ComponentProps<"span">
>(({ className, children, ...props }, ref) => {
  return (
    <span ref={ref} className={cn("wl-sub-title", className)} {...props}>
      {children}
    </span>
  );
});
SidebarSubTitle.displayName = "SidebarSubTitle";

const SidebarChevronIcon = React.forwardRef<
  HTMLElement,
  React.ComponentProps<"span">
>(({ className, children, ...props }, ref) => {
  return (
    <span
      ref={ref}
      className={cn("wl-icon wl-chevron-icon", className)}
      {...props}
    >
      {children}
    </span>
  );
});
SidebarChevronIcon.displayName = "SidebarChevronIcon";

const SidebarHeaderText = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, children, ...props }, ref) => {
  return (
    <div className="wl-d-inline-flex wl-align-items-center wl-gap-8">
      <div ref={ref} className={cn("wl-head-text", className)} {...props}>
        {children}
      </div>
    </div>
  );
});
SidebarHeaderText.displayName = "SidebarHeaderText";

// ============================================================================
// EXPORTS
// ============================================================================

export {
  Sidebar,
  SidebarHeader,
  SidebarBody,
  SidebarFooter,
  SidebarItem,
  SidebarItemTitle,
  SidebarItemAction,
  SidebarItemTitleList,
  SidebarAccordion,
  SidebarAccordionItem,
  SidebarAccordionToggle,
  SidebarAccordionItemList,
  SidebarHeadTop,
  SidebarWrapper,
  SidebarIconBlock,
  SidebarLogo,
  SidebarAvatar,
  SidebarInfoWrapper,
  SidebarSubTitle,
  SidebarChevronIcon,
  SidebarHeaderText,
};

export type {
  SidebarProps,
  SidebarHeaderProps,
  SidebarBodyProps,
  SidebarFooterProps,
  SidebarItemProps,
  SidebarItemTitleProps,
  SidebarItemActionProps,
  SidebarAccordionProps,
  SidebarAccordionItemProps,
  SidebarAccordionToggleProps,
  SidebarAccordionItemListProps,
};
